import { A } from '@ember/array';
import { assert, warn } from '@ember/debug';
import { scheduleOnce, schedule } from '@ember/runloop';
import { DEBUG } from '@glimmer/env';
import { default as RSVP, Promise } from 'rsvp';

// TODO @runspired symbol shouldn't be in ts-interfaces
// as it is runtime code
import { symbol } from '../ts-interfaces/utils/symbol';
import coerceId from './coerce-id';
import { errorsArrayToHash } from './errors-utils';
import RequestCache from './request-cache';
import Snapshot from './snapshot';
import { _bind, _guard, _objectIsAlive, guardDestroyedStore } from './store/common';
import { normalizeResponseHelper } from './store/serializer-response';
function payloadIsNotBlank(adapterPayload) {
  if (Array.isArray(adapterPayload)) {
    return true;
  } else {
    return Object.keys(adapterPayload || {}).length !== 0;
  }
}
export var SaveOp = symbol('SaveOp');
export default class FetchManager {
  constructor(_store) {
    this._store = _store;
    this.isDestroyed = void 0;
    this.requestCache = void 0;
    // saves which are pending in the runloop
    this._pendingSave = void 0;
    // fetches pending in the runloop, waiting to be coalesced
    this._pendingFetch = void 0;
    // used to keep track of all the find requests that need to be coalesced
    this._pendingFetch = new Map();
    this._pendingSave = [];
    this.requestCache = new RequestCache();
  }

  /**
    This method is called by `record.save`, and gets passed a
    resolver for the promise that `record.save` returns.
     It schedules saving to happen at the end of the run loop.
  */
  scheduleSave(identifier, options = {}) {
    var promiseLabel = 'DS: Model#save ' + this;
    var resolver = RSVP.defer(promiseLabel);
    var query = {
      op: 'saveRecord',
      recordIdentifier: identifier,
      options
    };
    var queryRequest = {
      data: [query]
    };
    var snapshot = new Snapshot(options, identifier, this._store);
    var pendingSaveItem = {
      snapshot: snapshot,
      resolver: resolver,
      identifier,
      options,
      queryRequest
    };
    this._pendingSave.push(pendingSaveItem);
    scheduleOnce('actions', this, this._flushPendingSaves);
    this.requestCache.enqueue(resolver.promise, pendingSaveItem.queryRequest);
    return resolver.promise;
  }
  _flushPendingSave(pending) {
    var {
      snapshot,
      resolver,
      identifier,
      options
    } = pending;
    var adapter = this._store.adapterFor(identifier.type);
    var operation = options[SaveOp];

    // TODO We have to cast due to our reliance on this private property
    // this will be refactored away once we change our pending API to be identifier based
    var internalModel = snapshot._internalModel;
    var modelName = snapshot.modelName;
    var store = this._store;
    var modelClass = store.modelFor(modelName);
    assert(`You tried to update a record but you have no adapter (for ${modelName})`, adapter);
    assert(`You tried to update a record but your adapter (for ${modelName}) does not implement '${operation}'`, typeof adapter[operation] === 'function');
    var promise = Promise.resolve().then(() => adapter[operation](store, modelClass, snapshot));
    var serializer = store.serializerFor(modelName);
    var label = `DS: Extract and notify about ${operation} completion of ${internalModel}`;
    assert(`Your adapter's '${operation}' method must return a value, but it returned 'undefined'`, promise !== undefined);
    promise = guardDestroyedStore(promise, store, label);
    promise = _guard(promise, _bind(_objectIsAlive, internalModel));
    promise = promise.then(adapterPayload => {
      if (adapterPayload) {
        return normalizeResponseHelper(serializer, store, modelClass, adapterPayload, snapshot.id, operation);
      }
    }, function (error) {
      if (error && error.isAdapterError === true && error.code === 'InvalidError') {
        var parsedErrors = error.errors;
        if (typeof serializer.extractErrors === 'function') {
          parsedErrors = serializer.extractErrors(store, modelClass, error, snapshot.id);
        } else {
          parsedErrors = errorsArrayToHash(error.errors);
        }
        throw {
          error,
          parsedErrors
        };
      } else {
        throw {
          error
        };
      }
    }, label);
    resolver.resolve(promise);
  }

  /**
    This method is called at the end of the run loop, and
    flushes any records passed into `scheduleSave`
     @method flushPendingSave
    @private
  */
  _flushPendingSaves() {
    var pending = this._pendingSave.slice();
    this._pendingSave = [];
    for (var i = 0, j = pending.length; i < j; i++) {
      var pendingItem = pending[i];
      this._flushPendingSave(pendingItem);
    }
  }
  scheduleFetch(identifier, options, shouldTrace) {
    // TODO Probably the store should pass in the query object

    var query = {
      op: 'findRecord',
      recordIdentifier: identifier,
      options
    };
    var queryRequest = {
      data: [query]
    };
    var pendingFetches = this._pendingFetch.get(identifier.type);

    // We already have a pending fetch for this
    if (pendingFetches) {
      var matchingPendingFetch = pendingFetches.find(fetch => fetch.identifier.id === identifier.id);
      if (matchingPendingFetch) {
        return matchingPendingFetch.resolver.promise;
      }
    }
    var id = identifier.id;
    var modelName = identifier.type;
    var resolver = RSVP.defer(`Fetching ${modelName}' with id: ${id}`);
    var pendingFetchItem = {
      identifier,
      resolver,
      options,
      queryRequest
    };
    if (DEBUG) {
      if (shouldTrace) {
        var trace;
        try {
          throw new Error(`Trace Origin for scheduled fetch for ${modelName}:${id}.`);
        } catch (e) {
          trace = e;
        }

        // enable folks to discover the origin of this findRecord call when
        // debugging. Ideally we would have a tracked queue for requests with
        // labels or local IDs that could be used to merge this trace with
        // the trace made available when we detect an async leak
        pendingFetchItem.trace = trace;
      }
    }
    var promise = resolver.promise;
    if (this._pendingFetch.size === 0) {
      schedule('actions', this, this.flushAllPendingFetches);
    }
    var fetches = this._pendingFetch;
    if (!fetches.has(modelName)) {
      fetches.set(modelName, []);
    }
    fetches.get(modelName).push(pendingFetchItem);
    this.requestCache.enqueue(promise, pendingFetchItem.queryRequest);
    return promise;
  }
  _fetchRecord(fetchItem) {
    var identifier = fetchItem.identifier;
    var modelName = identifier.type;
    var adapter = this._store.adapterFor(modelName);
    assert(`You tried to find a record but you have no adapter (for ${modelName})`, adapter);
    assert(`You tried to find a record but your adapter (for ${modelName}) does not implement 'findRecord'`, typeof adapter.findRecord === 'function');
    var snapshot = new Snapshot(fetchItem.options, identifier, this._store);
    var klass = this._store.modelFor(identifier.type);
    var promise = Promise.resolve().then(() => {
      return adapter.findRecord(this._store, klass, identifier.id, snapshot);
    });
    var id = identifier.id;
    var label = `DS: Handle Adapter#findRecord of '${modelName}' with id: '${id}'`;
    promise = guardDestroyedStore(promise, this._store, label);
    promise = promise.then(adapterPayload => {
      assert(`You made a 'findRecord' request for a '${modelName}' with id '${id}', but the adapter's response did not have any data`, !!payloadIsNotBlank(adapterPayload));
      var serializer = this._store.serializerFor(modelName);
      var payload = normalizeResponseHelper(serializer, this._store, klass, adapterPayload, id, 'findRecord');
      assert(`Ember Data expected the primary data returned from a 'findRecord' response to be an object but instead it found an array.`, !Array.isArray(payload.data));
      warn(`You requested a record of type '${modelName}' with id '${id}' but the adapter returned a payload with primary data having an id of '${payload.data.id}'. Use 'store.findRecord()' when the requested id is the same as the one returned by the adapter. In other cases use 'store.queryRecord()' instead.`, coerceId(payload.data.id) === coerceId(id), {
        id: 'ds.store.findRecord.id-mismatch'
      });
      return payload;
    }, error => {
      throw error;
    }, `DS: Extract payload of '${modelName}'`);
    fetchItem.resolver.resolve(promise);
  }

  // TODO should probably refactor expectedSnapshots to be identifiers
  handleFoundRecords(seeking, coalescedPayload, expectedSnapshots) {
    // resolve found records
    var found = Object.create(null);
    var payloads = coalescedPayload.data;
    var coalescedIncluded = coalescedPayload.included || [];
    for (var i = 0, l = payloads.length; i < l; i++) {
      var payload = payloads[i];
      var pair = seeking[payload.id];
      found[payload.id] = payload;
      var included = coalescedIncluded.concat(payloads);

      // TODO remove original data from included
      if (pair) {
        var resolver = pair.resolver;
        resolver.resolve({
          data: payload,
          included
        });
      }
    }

    // reject missing records

    // TODO NOW clean this up to refer to payloads
    var missingSnapshots = [];
    for (var _i = 0, _l = expectedSnapshots.length; _i < _l; _i++) {
      var snapshot = expectedSnapshots[_i];
      assertIsString(snapshot.id);

      // We know id is a string because you can't fetch
      // without one.
      if (!found[snapshot.id]) {
        missingSnapshots.push(snapshot);
      }
    }
    if (missingSnapshots.length) {
      warn('Ember Data expected to find records with the following ids in the adapter response but they were missing: [ "' + missingSnapshots.map(r => r.id).join('", "') + '" ]', false, {
        id: 'ds.store.missing-records-from-adapter'
      });
      this.rejectFetchedItems(seeking, missingSnapshots);
    }
  }
  rejectFetchedItems(seeking, snapshots, error) {
    for (var i = 0, l = snapshots.length; i < l; i++) {
      var snapshot = snapshots[i];
      assertIsString(snapshot.id);
      // TODO refactor to identifier.lid to avoid this cast to string
      //  we can do this case because you can only fetch an identifier
      //  that has an ID
      var pair = seeking[snapshot.id];
      if (pair) {
        pair.resolver.reject(error || new Error(`Expected: '<${snapshot.modelName}:${snapshot.id}>' to be present in the adapter provided payload, but it was not found.`));
      }
    }
  }
  _findMany(adapter, store, modelName, snapshots, identifiers, optionsMap) {
    var modelClass = store.modelFor(modelName); // `adapter.findMany` gets the modelClass still
    var ids = snapshots.map(s => s.id);
    var promise = adapter.findMany(store, modelClass, ids, A(snapshots));
    var label = `DS: Handle Adapter#findMany of '${modelName}'`;
    if (promise === undefined) {
      throw new Error('adapter.findMany returned undefined, this was very likely a mistake');
    }
    promise = guardDestroyedStore(promise, store, label);
    return promise.then(adapterPayload => {
      assert(`You made a 'findMany' request for '${modelName}' records with ids '[${ids}]', but the adapter's response did not have any data`, !!payloadIsNotBlank(adapterPayload));
      var serializer = store.serializerFor(modelName);
      var payload = normalizeResponseHelper(serializer, store, modelClass, adapterPayload, null, 'findMany');
      return payload;
    }, null, `DS: Extract payload of ${modelName}`);
  }
  _processCoalescedGroup(seeking, group, adapter, optionsMap, modelName) {
    //TODO check what happened with identifiers here
    var totalInGroup = group.length;
    var ids = new Array(totalInGroup);
    var groupedSnapshots = new Array(totalInGroup);
    for (var j = 0; j < totalInGroup; j++) {
      groupedSnapshots[j] = group[j];
      ids[j] = groupedSnapshots[j].id;
    }
    var store = this._store;
    if (totalInGroup > 1) {
      this._findMany(adapter, store, modelName, group, groupedSnapshots, optionsMap).then(payloads => {
        this.handleFoundRecords(seeking, payloads, groupedSnapshots);
      }).catch(error => {
        this.rejectFetchedItems(seeking, groupedSnapshots, error);
      });
    } else if (ids.length === 1) {
      var pair = seeking[groupedSnapshots[0].id];
      this._fetchRecord(pair);
    } else {
      assert("You cannot return an empty array from adapter's method groupRecordsForFindMany", false);
    }
  }
  _flushPendingFetchForType(pendingFetchItems, modelName) {
    var adapter = this._store.adapterFor(modelName);
    var shouldCoalesce = !!adapter.findMany && adapter.coalesceFindRequests;
    var totalItems = pendingFetchItems.length;
    var identifiers = new Array(totalItems);
    var seeking = Object.create(null);
    var optionsMap = new WeakMap();
    for (var i = 0; i < totalItems; i++) {
      var pendingItem = pendingFetchItems[i];
      var identifier = pendingItem.identifier;
      identifiers[i] = identifier;
      optionsMap.set(identifier, pendingItem.options);
      seeking[identifier.id] = pendingItem;
    }
    if (shouldCoalesce) {
      // TODO: Improve records => snapshots => records => snapshots
      //
      // We want to provide records to all store methods and snapshots to all
      // adapter methods. To make sure we're doing that we're providing an array
      // of snapshots to adapter.groupRecordsForFindMany(), which in turn will
      // return grouped snapshots instead of grouped records.
      //
      // But since the _findMany() finder is a store method we need to get the
      // records from the grouped snapshots even though the _findMany() finder
      // will once again convert the records to snapshots for adapter.findMany()
      var snapshots = new Array(totalItems);
      for (var _i2 = 0; _i2 < totalItems; _i2++) {
        // we know options is in the map due to having just set it above
        // but TS doesn't know so we cast it
        var options = optionsMap.get(identifiers[_i2]);
        snapshots[_i2] = new Snapshot(options, identifiers[_i2], this._store);
      }
      var groups = adapter.groupRecordsForFindMany(this, snapshots);
      for (var _i3 = 0, l = groups.length; _i3 < l; _i3++) {
        this._processCoalescedGroup(seeking, groups[_i3], adapter, optionsMap, modelName);
      }
    } else {
      for (var _i4 = 0; _i4 < totalItems; _i4++) {
        this._fetchRecord(pendingFetchItems[_i4]);
      }
    }
  }
  flushAllPendingFetches() {
    if (this.isDestroyed) {
      return;
    }
    this._pendingFetch.forEach(this._flushPendingFetchForType, this);
    this._pendingFetch.clear();
  }
  destroy() {
    this.isDestroyed = true;
  }
}
function assertIsString(id) {
  if (DEBUG) {
    if (typeof id !== 'string') {
      throw new Error(`Cannot fetch record without an id`);
    }
  }
}